import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './ProfileSignature.css';

export default function ProfileSignature() {
    const { t } = useLanguage();
    const [status, setStatus] = useState(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState('');
    const [saved, setSaved] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const input = useRef(null);
    useEffect(() => {
        let active = true; let objectUrl = '';
        api.getProfileSignature().then(async (r) => {
            if (!active) return;
            setStatus(r.data);
            if (r.data.has_signature) {
                objectUrl = URL.createObjectURL(await api.profileSignatureBlob());
                if (active) setSaved(objectUrl); else URL.revokeObjectURL(objectUrl);
            }
        }).catch((e) => { if (active) setError(e.message); });
        return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, []);
    useEffect(() => {
        if (!file) { setPreview(''); return undefined; }
        const url = URL.createObjectURL(file); setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    useEffect(() => () => { if (saved) URL.revokeObjectURL(saved); }, [saved]);
    const upload = async () => {
        if (!file) return;
        setBusy(true); setError(''); setMessage('');
        try {
            const r = await api.uploadProfileSignature(file); setStatus(r.data);
            const url = URL.createObjectURL(await api.profileSignatureBlob());
            setSaved((old) => { if (old) URL.revokeObjectURL(old); return url; });
            setFile(null); if (input.current) input.current.value = '';
            setMessage(t('Signature saved. It will be added when you sign or approve a document.'));
        } catch (e) { setError(t(e.message)); } finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t('Remove your current signature? Previously signed documents will keep their signatures.'))) return;
        setBusy(true); setError(''); setMessage('');
        try { const r = await api.removeProfileSignature(); setStatus(r.data); setSaved((old) => { if (old) URL.revokeObjectURL(old); return ''; }); setFile(null); if (input.current) input.current.value = ''; }
        catch (e) { setError(t(e.message)); } finally { setBusy(false); }
    };
    if (status && !status.eligible) return null;
    if (!status && !error) return null;
    return <section className="profile-signature-panel">
        <h2>{t('My signature')}</h2>
        <p>{t('Upload a clear photo or scan of your signature. Use PNG or JPG, up to 2 MB. A transparent PNG or a plain white background works well.')}</p>
        {(preview || saved) && <div className="profile-signature-preview"><img src={preview || saved} alt={t('Signature preview')} /></div>}
        {status?.uploaded_at && <small>{t('Last uploaded')}: {status.uploaded_at}</small>}
        <label>{t('Choose signature image')}<input ref={input} type="file" accept="image/png,image/jpeg" disabled={busy} onChange={(e) => { setError(''); setMessage(''); const next = e.target.files?.[0]; if (next && next.size > 2 * 1024 * 1024) { setError(t('The signature image must be 2 MB or smaller.')); setFile(null); e.target.value = ''; return; } setFile(next || null); }} /></label>
        <div className="profile-signature-actions"><button type="button" disabled={busy || !file} onClick={upload}>{busy ? t('Saving...') : t('Save signature')}</button>
            {status?.has_signature && <button type="button" disabled={busy} onClick={remove}>{t('Remove signature')}</button>}</div>
        <p>{t('Your signature is added only when you sign, submit or approve a document yourself. Changing it later does not change documents already signed.')}</p>
        {error && <p role="alert" className="profile-signature-error">{error}</p>}{message && <p role="status">{message}</p>}
    </section>;
}
